import React, { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import {
  OfflineContentItem,
  OfflineContentItemNoTOC,
} from '../OfflineContentItem';
import { doesMapMatch } from '../OfflineHelpers';
import { ChapterList, LoadingButton } from 'components';
import { OFFLINE_CHAPTER_STATUS } from 'constants';
import { Typography } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      ...theme.typography.body2,
    },
    sectionHeaderWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontWeight: theme.typography.fontWeightMedium,
      borderTop: ({ isAnyMarkerDownloaded }) => {
        if (isAnyMarkerDownloaded) {
          return `1px solid ${theme.palette.grey[300]}`;
        }
        return 'none';
      },
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    sectionHeader: {
      color: theme.palette.greyBlue[700],
      padding: theme.spacing(theme.typography.pxToRem(11.5), 2),
      fontWeight: theme.typography.fontWeightMedium,
    },
  };
});

const arePropsEqual = (prevProps, nextProps) => {
  return (
    doesMapMatch(prevProps.markerStatusMap, nextProps.markerStatusMap) &&
    prevProps.showDownloadAll === nextProps.showDownloadAll &&
    prevProps.isLoading === nextProps.isLoading
  );
};

const ContentAvailableToDownload = ({
  availableContent,
  className,
  contentToDownloadItemsRef,
  downloadAll,
  isAnyMarkerDownloaded,
  isLoading,
  markerStatusMap,
  noTOCBookDownload,
  onClose,
  showDownloadAll,
  statusButtonOnClick,
  ...props
}) => {
  const classes = useStyles({
    isAnyMarkerDownloaded,
  });

  const showChaptersList = availableContent.length > 0;
  const showContentAvailableToDownload = showChaptersList || noTOCBookDownload;

  return showContentAvailableToDownload ? (
    <div className={clsx(classes.root, className)} {...props}>
      <div className={classes.sectionHeaderWrapper}>
        <Typography
          component="h3"
          variant="body2"
          className={classes.sectionHeader}
        >
          Content Available to Download
        </Typography>

        {showDownloadAll && (
          <LoadingButton isLoading={isLoading} onClick={downloadAll}>
            Download All
          </LoadingButton>
        )}
      </div>

      {showChaptersList && (
        <ChapterList ref={contentToDownloadItemsRef}>
          {availableContent.map((marker) => (
            <React.Fragment
              key={`${marker.label}-${marker.index}-${marker.id}`}
            >
              <OfflineContentItem
                marker={marker}
                markerStatusMap={markerStatusMap}
                onClose={onClose}
                statusButtonOnClick={statusButtonOnClick}
              />
            </React.Fragment>
          ))}
        </ChapterList>
      )}

      {noTOCBookDownload && (
        <OfflineContentItemNoTOC
          status={OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED}
          statusButtonOnClick={downloadAll}
        />
      )}
    </div>
  ) : null;
};

ContentAvailableToDownload.propTypes = {
  availableContent: PropTypes.array,
  className: PropTypes.string,
  contentToDownloadItemsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  downloadAll: PropTypes.func,
  isAnyMarkerDownloaded: PropTypes.bool,
  isLoading: PropTypes.bool,
  markerStatusMap: PropTypes.instanceOf(Map),
  noTOCBookDownload: PropTypes.bool,
  onClose: PropTypes.func,
  showDownloadAll: PropTypes.bool,
  statusButtonOnClick: PropTypes.func,
};

export default memo(ContentAvailableToDownload, arePropsEqual);
